import {
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Collapse,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { colors, alphaVar } from "../../../../../ui/colors";
import { useDialog } from "../../../../../ui/dialogs/useDialog";
import type { InvoiceTipKind } from "../actions/setInvoiceTip";
import { useSetInvoiceTip } from "../hooks/useSetInvoiceTip";

/**
 * TIP RULES & GUARDRAILS:
 * 1. Optional & Unobtrusive by Default:
 *    - Collapsed by default into a discreet "+ Add tip (optional)" action to avoid feeling pushy.
 * 2. Presets & Quick Exit:
 *    - Quick percentage preset tiles: 10%, 15%, 20%.
 *    - Direct "No tip ($0.00)" tile to instantly clear tip and collapse the card without friction.
 * 3. Security Limits & Hard Caps:
 *    - Maximum percentage cap: 50% (MAX_PERCENT_CAP).
 *    - Maximum dollar amount cap: 100% of invoice total (or $1,000 max).
 *    - Negative numbers are disallowed; typing 0 or 0% automatically clears the tip.
 * 4. Real-time Live Calculation Feedback:
 *    - As the user types in custom mode, dynamically computes and previews the dollar/percent equivalent and new total.
 * 5. High-Tip Confirmation Dialog (Safety Check):
 *    - If a custom tip >= 30% (or >= $100 with >= 25%) is submitted, a confirmation modal prompts
 *      the client to verify the amount to prevent accidental extra zeros or typos.
 * 6. Order Reactivity:
 *    - Updating or clearing tips updates the total balance and automatically triggers a refreshed payment order for Stripe / PayPal.
 */

const PERCENT_PRESETS = [10, 15, 20];
const MAX_PERCENT_CAP = 50; // Maximum allowed percentage (50%)
const HIGH_TIP_PERCENT_THRESHOLD = 30; // Threshold for safety confirmation dialog

type Props = {
  invoiceUid: string;
  invoiceTotal: number;
  tipAmount: number;
  tipKind?: InvoiceTipKind | null;
  tipValue?: number | null;
  disabled?: boolean;
};

function formatMoney(n: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

export function InvoiceClientTipCard({ invoiceUid, invoiceTotal, tipAmount, tipKind, tipValue, disabled = false }: Props) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || sessionStorage.getItem("token");
  const { setInvoiceTip, isSavingTip } = useSetInvoiceTip(invoiceUid);
  const { showDialog, DialogComponent } = useDialog();

  const hasActiveTip = tipAmount > 0;

  // Track if selection UI is expanded
  const [isExpanded, setIsExpanded] = useState(false);
  const [isCustomMode, setIsCustomMode] = useState(false);

  const [kind, setKind] = useState<InvoiceTipKind>(tipKind === "amount" ? "amount" : "percentage");
  const [customValue, setCustomValue] = useState(
    tipValue != null && tipValue > 0 && (!tipKind || tipKind !== "percentage" || !PERCENT_PRESETS.includes(Number(tipValue))) ? String(tipValue) : "",
  );

  const isCustomActive = useMemo(() => {
    if (!hasActiveTip) return false;
    if (tipKind === "amount") return true;
    if (tipKind === "percentage" && !PERCENT_PRESETS.includes(Number(tipValue))) return true;
    return false;
  }, [hasActiveTip, tipKind, tipValue]);

  const selectedPercent = useMemo(() => {
    if (tipKind === "percentage" && PERCENT_PRESETS.includes(Number(tipValue)) && hasActiveTip) {
      return Number(tipValue);
    }
    return null;
  }, [tipKind, tipValue, hasActiveTip]);

  // Keep customValue in sync if prop changes
  useEffect(() => {
    if (isCustomActive && tipValue != null && tipValue > 0) {
      setCustomValue(String(tipValue));
    }
  }, [isCustomActive, tipValue]);

  // Parsed custom value
  const parsedCustomVal = useMemo(() => {
    const clean = String(customValue ?? "").trim();
    if (!clean) return 0;
    const num = Number.parseFloat(clean);
    return Number.isFinite(num) ? num : null;
  }, [customValue]);

  // Validation limits
  const maxDollarCap = useMemo(() => {
    return invoiceTotal > 0 ? invoiceTotal : 1000;
  }, [invoiceTotal]);

  const customValidationError = useMemo(() => {
    if (parsedCustomVal === null) return "Please enter a valid numeric value.";
    if (parsedCustomVal < 0) return "Negative amounts are not allowed.";
    if (kind === "percentage" && parsedCustomVal > MAX_PERCENT_CAP) {
      return `Maximum tip is ${MAX_PERCENT_CAP}%.`;
    }
    if (kind === "amount" && parsedCustomVal > maxDollarCap) {
      return `Maximum tip is ${formatMoney(maxDollarCap)} (100% of invoice total).`;
    }
    return null;
  }, [parsedCustomVal, kind, maxDollarCap]);

  // Live real-time calculation preview
  const livePreview = useMemo(() => {
    if (parsedCustomVal === null || parsedCustomVal <= 0 || customValidationError) return null;

    let dollarAmount = 0;
    let percentEquivalent = 0;

    if (kind === "percentage") {
      percentEquivalent = parsedCustomVal;
      dollarAmount = invoiceTotal > 0 ? (invoiceTotal * parsedCustomVal) / 100 : 0;
    } else {
      dollarAmount = parsedCustomVal;
      percentEquivalent = invoiceTotal > 0 ? (parsedCustomVal / invoiceTotal) * 100 : 0;
    }

    const newTotal = (invoiceTotal > 0 ? invoiceTotal : 0) + dollarAmount;
    const isHighTip = percentEquivalent >= HIGH_TIP_PERCENT_THRESHOLD || dollarAmount >= 100;

    return {
      dollarAmount,
      percentEquivalent,
      newTotal,
      isHighTip,
    };
  }, [parsedCustomVal, customValidationError, kind, invoiceTotal]);

  const saveTip = async (nextKind: InvoiceTipKind, nextValue: number) => {
    await setInvoiceTip({ invoiceUid, kind: nextKind, value: nextValue, token });
    if (nextValue === 0) {
      setIsExpanded(false);
      setIsCustomMode(false);
    }
  };

  const handleSelectPreset = (percent: number) => {
    if (disabled || isSavingTip) return;
    setIsCustomMode(false);
    setKind("percentage");
    setCustomValue(String(percent));
    void saveTip("percentage", percent);
  };

  const handleClearTip = () => {
    if (disabled || isSavingTip) return;
    setIsCustomMode(false);
    setIsExpanded(false);
    setCustomValue("");
    void saveTip("amount", 0);
  };

  const handleApplyCustom = () => {
    if (disabled || isSavingTip) return;

    if (parsedCustomVal === null || parsedCustomVal <= 0) {
      handleClearTip();
      return;
    }

    if (customValidationError) {
      return;
    }

    const dollarAmount = kind === "percentage" ? (invoiceTotal > 0 ? (invoiceTotal * parsedCustomVal) / 100 : 0) : parsedCustomVal;

    const percentEquivalent = kind === "percentage" ? parsedCustomVal : invoiceTotal > 0 ? (parsedCustomVal / invoiceTotal) * 100 : 0;

    const isHighTip = percentEquivalent >= HIGH_TIP_PERCENT_THRESHOLD || (dollarAmount >= 100 && percentEquivalent >= 25);

    // Safety Confirmation Dialog for High Tips
    if (isHighTip) {
      showDialog({
        type: "confirm",
        title: "Confirm Tip Amount",
        message: `You are adding a tip of ${formatMoney(dollarAmount)} (${percentEquivalent.toFixed(1)}% of invoice total). Is this amount correct?`,
        confirmLabel: `Add ${formatMoney(dollarAmount)}`,
        cancelLabel: "Edit amount",
        onConfirm: () => void saveTip(kind, parsedCustomVal),
      });
      return;
    }

    void saveTip(kind, parsedCustomVal);
  };

  // 1. If a tip is active AND not editing: render a polished, clean badge
  if (hasActiveTip && !isExpanded) {
    return (
      <>
        <Box
          sx={{
            mt: 1,
            mb: 0.5,
            display: "inline-flex",
            alignItems: "center",
            gap: 1.25,
            px: 1.25,
            py: 0.5,
            borderRadius: "8px",
            bgcolor: alphaVar("#10B981", 0.08),
            border: `1px solid ${alphaVar("#10B981", 0.25)}`,
            maxWidth: "fit-content",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FavoriteRoundedIcon sx={{ fontSize: 13, color: "#10B981" }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                fontSize: "0.76rem",
                color: "#065F46",
                letterSpacing: "-0.01em",
              }}
            >
              Tip added: +{formatMoney(tipAmount)}
              {tipKind === "percentage" && tipValue ? ` (${tipValue}%)` : ""}
            </Typography>
          </Box>

          {isSavingTip ? (
            <CircularProgress size={11} sx={{ color: "#10B981" }} />
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
              <Button
                size="small"
                variant="text"
                disabled={disabled || isSavingTip}
                onClick={() => {
                  setIsExpanded(true);
                  if (isCustomActive) setIsCustomMode(true);
                }}
                sx={{
                  minWidth: 0,
                  p: 0,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  textTransform: "none",
                  color: "#059669",
                  "&:hover": { color: "#047857", bgcolor: "transparent", textDecoration: "underline" },
                }}
              >
                Edit
              </Button>
              <Typography variant="caption" sx={{ color: alphaVar("#065F46", 0.3), fontSize: "0.7rem" }}>
                ·
              </Typography>
              <Button
                size="small"
                variant="text"
                disabled={disabled || isSavingTip}
                onClick={handleClearTip}
                sx={{
                  minWidth: 0,
                  p: 0,
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  textTransform: "none",
                  color: colors.gray[500],
                  "&:hover": { color: "#EF4444", bgcolor: "transparent", textDecoration: "underline" },
                }}
              >
                Remove
              </Button>
            </Box>
          )}
        </Box>
        {DialogComponent}
      </>
    );
  }

  // 2. If no tip is active AND not expanded: understated minimalist trigger link
  if (!isExpanded) {
    return (
      <>
        <Box sx={{ mt: 1, mb: 0.5, display: "flex", alignItems: "center" }}>
          <Button
            size="small"
            variant="text"
            disabled={disabled || isSavingTip}
            onClick={() => setIsExpanded(true)}
            startIcon={<FavoriteBorderRoundedIcon sx={{ fontSize: "14px !important", color: colors.gray[500] }} />}
            sx={{
              color: colors.gray[600],
              fontSize: "0.775rem",
              fontWeight: 600,
              textTransform: "none",
              p: 0,
              minWidth: 0,
              "&:hover": {
                bgcolor: "transparent",
                color: colors.gray[900],
                textDecoration: "underline",
              },
            }}
          >
            Add a tip (optional)
          </Button>
          {isSavingTip && <CircularProgress size={12} sx={{ ml: 1, color: colors.blue[500] }} />}
        </Box>
        {DialogComponent}
      </>
    );
  }

  // 3. When expanded: modern card with compact 5-column mini cards
  return (
    <>
      <Box
        sx={{
          mt: 1.25,
          mb: 0.75,
          p: { xs: 1.25, sm: 1.25 },
          borderRadius: "12px",
          bgcolor: "var(--fx-surface-base, #FFFFFF)",
          border: `1px solid ${alphaVar(colors.gray[200] || "var(--fx-surface-border, #E2E8F0)", 0.9)}`,
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.03)",
          display: "flex",
          flexDirection: "column",
          gap: 1,
          maxWidth: 480,
        }}
      >
        {/* Header of expanded selector */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
            <Box
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: alphaVar(colors.blue[500], 0.1),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FavoriteRoundedIcon sx={{ fontSize: 11, color: colors.blue[600] }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 750, fontSize: "0.78rem", color: colors.gray[800] }}>
              Add a tip
            </Typography>
            <Typography variant="caption" sx={{ color: colors.gray[400], fontSize: "0.72rem", fontWeight: 500 }}>
              (Optional)
            </Typography>
            {isSavingTip && <CircularProgress size={11} sx={{ ml: 0.5, color: colors.blue[500] }} />}
          </Box>

          <IconButton
            size="small"
            disabled={disabled || isSavingTip}
            onClick={() => {
              setIsExpanded(false);
              setIsCustomMode(false);
            }}
            sx={{
              p: 0.25,
              color: colors.gray[400],
              "&:hover": { color: colors.gray[700], bgcolor: alphaVar(colors.gray[100], 0.6) },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Box>

        {/* Preset Tiles Grid (5 Columns) */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 0.75,
          }}
        >
          {/* No Tip Tile */}
          <ButtonBase
            disabled={disabled || isSavingTip}
            onClick={handleClearTip}
            sx={{
              py: 0.6,
              px: 0.25,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: !hasActiveTip ? alphaVar(colors.gray[200], 0.6) : "var(--fx-surface-sunken, #F8FAFC)",
              border: !hasActiveTip ? `1px solid ${colors.gray[300] || "#CBD5E1"}` : `1px solid ${colors.gray[200] || "#E2E8F0"}`,
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: !hasActiveTip ? alphaVar(colors.gray[200], 0.8) : "var(--fx-surface-muted, #F1F5F9)",
                borderColor: colors.gray[300],
                transform: "translateY(-1px)",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.76rem",
                lineHeight: 1.15,
                color: !hasActiveTip ? colors.gray[800] : colors.gray[600],
              }}
            >
              No tip
            </Typography>
            <Typography
              sx={{
                mt: 0.2,
                fontSize: "0.625rem",
                fontWeight: 600,
                lineHeight: 1,
                color: colors.gray[400],
              }}
            >
              $0.00
            </Typography>
          </ButtonBase>

          {/* 10%, 15%, 20% Preset Tiles */}
          {PERCENT_PRESETS.map((percent) => {
            const isSelected = selectedPercent === percent;
            const calculatedDollar = invoiceTotal > 0 ? (invoiceTotal * percent) / 100 : null;

            return (
              <ButtonBase
                key={percent}
                disabled={disabled || isSavingTip}
                onClick={() => handleSelectPreset(percent)}
                sx={{
                  py: 0.6,
                  px: 0.25,
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  bgcolor: isSelected ? alphaVar(colors.blue[500], 0.08) : "var(--fx-surface-sunken, #F8FAFC)",
                  border: isSelected ? `1.5px solid ${colors.blue[500] || "#3B82F6"}` : `1px solid ${colors.gray[200] || "#E2E8F0"}`,
                  boxShadow: isSelected ? `0 0 0 1px ${colors.blue[500] || "#3B82F6"}` : "none",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    bgcolor: isSelected ? alphaVar(colors.blue[500], 0.12) : "var(--fx-surface-muted, #F1F5F9)",
                    borderColor: isSelected ? colors.blue[600] : colors.gray[300],
                    transform: "translateY(-1px)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: "0.76rem",
                    lineHeight: 1.15,
                    color: isSelected ? colors.blue[700] || "#1D4ED8" : colors.gray[800],
                  }}
                >
                  {percent}%
                </Typography>
                {calculatedDollar != null && calculatedDollar > 0 && (
                  <Typography
                    sx={{
                      mt: 0.2,
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      lineHeight: 1,
                      color: isSelected ? colors.blue[600] || "#2563EB" : colors.gray[500],
                    }}
                  >
                    {formatMoney(calculatedDollar)}
                  </Typography>
                )}
              </ButtonBase>
            );
          })}

          {/* Custom Option Tile */}
          <ButtonBase
            disabled={disabled || isSavingTip}
            onClick={() => setIsCustomMode((prev) => !prev)}
            sx={{
              py: 0.6,
              px: 0.25,
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: isCustomActive ? alphaVar(colors.blue[500], 0.08) : isCustomMode ? alphaVar(colors.gray[100], 0.9) : "#F8FAFC",
              border: isCustomActive
                ? `1.5px solid ${colors.blue[500] || "#3B82F6"}`
                : isCustomMode
                  ? `1.5px solid ${colors.gray[300] || "#CBD5E1"}`
                  : `1px solid ${colors.gray[200] || "#E2E8F0"}`,
              boxShadow: isCustomActive ? `0 0 0 1px ${colors.blue[500] || "#3B82F6"}` : "none",
              transition: "all 0.15s ease",
              "&:hover": {
                bgcolor: isCustomActive ? alphaVar(colors.blue[500], 0.12) : "var(--fx-surface-muted, #F1F5F9)",
                borderColor: isCustomActive ? colors.blue[600] : colors.gray[300],
                transform: "translateY(-1px)",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: "0.76rem",
                lineHeight: 1.15,
                color: isCustomActive ? colors.blue[700] || "#1D4ED8" : colors.gray[800],
              }}
            >
              Custom
            </Typography>
            <Typography
              sx={{
                mt: 0.2,
                fontSize: "0.625rem",
                fontWeight: 600,
                lineHeight: 1,
                color: isCustomActive ? colors.blue[600] || "#2563EB" : colors.gray[500],
              }}
            >
              {isCustomActive && tipAmount > 0 ? (tipKind === "percentage" ? `${tipValue}%` : formatMoney(tipAmount)) : "Other"}
            </Typography>
          </ButtonBase>
        </Box>

        {/* Inline Custom Input with Live Feedback */}
        <Collapse in={isCustomMode} timeout={160}>
          <Box
            sx={{
              pt: 0.5,
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={kind}
                disabled={disabled || isSavingTip}
                onChange={(_, val: InvoiceTipKind | null) => {
                  if (val) setKind(val);
                }}
                sx={{
                  height: 28,
                  "& .MuiToggleButton-root": {
                    px: 1,
                    py: 0,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    borderColor: colors.gray[200] || "var(--fx-surface-border, #E2E8F0)",
                    "&.Mui-selected": {
                      bgcolor: alphaVar(colors.blue[500], 0.1),
                      color: colors.blue[700] || "#1D4ED8",
                      borderColor: colors.blue[300] || "#93C5FD",
                    },
                  },
                }}
              >
                <ToggleButton value="percentage">%</ToggleButton>
                <ToggleButton value="amount">$</ToggleButton>
              </ToggleButtonGroup>

              <TextField
                size="small"
                type="number"
                disabled={disabled || isSavingTip}
                placeholder={kind === "percentage" ? "e.g. 18" : "e.g. 15.00"}
                value={customValue}
                error={Boolean(customValidationError)}
                onChange={(e) => setCustomValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleApplyCustom();
                  }
                }}
                inputProps={{
                  min: 0,
                  step: kind === "percentage" ? "1" : "0.50",
                  max: kind === "percentage" ? MAX_PERCENT_CAP : maxDollarCap,
                }}
                sx={{
                  width: { xs: 100, sm: 120 },
                  "& .MuiInputBase-root": {
                    height: 28,
                    fontSize: "0.76rem",
                    borderRadius: "6px",
                    bgcolor: "var(--fx-surface-base, #FFFFFF)",
                  },
                  "& .MuiOutlinedInput-input": {
                    py: 0.2,
                    px: 0.75,
                  },
                }}
              />

              <Button
                size="small"
                variant="contained"
                color="primary"
                disabled={disabled || isSavingTip || !customValue || Boolean(customValidationError)}
                onClick={handleApplyCustom}
                sx={{
                  height: 28,
                  minWidth: 54,
                  px: 1.25,
                  borderRadius: "6px",
                  textTransform: "none",
                  fontSize: "0.72rem",
                  fontWeight: 750,
                  boxShadow: "none",
                }}
              >
                Apply
              </Button>
            </Box>

            {/* Real-time Dynamic Calculation Preview */}
            {livePreview && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 0.5,
                  px: 0.85,
                  py: 0.4,
                  borderRadius: "6px",
                  bgcolor: livePreview.isHighTip ? alphaVar("#F97316", 0.08) : alphaVar("#3B82F6", 0.06),
                  border: `1px solid ${livePreview.isHighTip ? alphaVar("#F97316", 0.25) : alphaVar("#3B82F6", 0.18)}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  {livePreview.isHighTip && <WarningAmberRoundedIcon sx={{ fontSize: 13, color: "#EA580C" }} />}
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 750,
                      color: livePreview.isHighTip ? "#C2410C" : "#1D4ED8",
                    }}
                  >
                    {kind === "percentage"
                      ? `≈ +${formatMoney(livePreview.dollarAmount)}`
                      : `≈ ${livePreview.percentEquivalent.toFixed(1)}% of total`}
                    {livePreview.isHighTip ? " (High tip)" : ""}
                  </Typography>
                </Box>

                <Typography variant="caption" sx={{ fontSize: "0.68rem", fontWeight: 600, color: colors.gray[600] }}>
                  New total: {formatMoney(livePreview.newTotal)}
                </Typography>
              </Box>
            )}

            {/* Validation Error Inline */}
            {customValidationError && (
              <Typography
                variant="caption"
                sx={{
                  px: 0.5,
                  color: "#DC2626",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                }}
              >
                {customValidationError}
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
      {DialogComponent}
    </>
  );
}
